import React, { useState, useEffect } from 'react';
import Classes from './Reports.module.css';
import useScreenWidth from '../../../../../Hooks/useScreenWidth';
import SkeletonLoading from '../../../../GlobalComponents/SkeletonLoading/SkeletonLoading';
import { Pagination, Stack, IconButton, Modal, Box, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import backend from '../../../../../api';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import jsPDF from 'jspdf';
import TableRow from './TableRow';

const AddIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" {...props} viewBox="0 0 448 512">
    <path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 144L48 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l144 0 0 144c0 17.7 14.3 32 32 32s32-14.3 32-32l0-144 144 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-144 0 0-144z" />
  </svg>
);

export default function Reports() {
  const { user } = useSelector((state) => state.auth);
  const userId = user?._id;
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openModal, setOpenModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const navigateTo = useNavigate();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 600;

  const location = useLocation();
  const [activePage, setActivePage] = useState(location.pathname);

  useEffect(() => {
    setActivePage(location.pathname);
  }, [location.pathname]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const response = await backend.get(`/user-information?userId=${userId}`);
        setReports(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching reports:', error);
        setLoading(false);
      }
    };
    fetchReports();
  }, [userId]);

  const groupReportsByMonth = (reports) => {
    const grouped = {};
    reports.forEach((report) => {
      const date = new Date(report.createdAt);
      const monthYear = `${date.toLocaleString('default', { month: 'long' })}-${date.getFullYear()}`;
      if (!grouped[monthYear]) {
        grouped[monthYear] = [];
      }
      grouped[monthYear].push(report);
    });
    return grouped;
  };

  const monthlyReports = groupReportsByMonth(reports);

  const filteredMonthlyReports = Object.keys(monthlyReports).filter((monthYear) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = (
      monthYear.toLowerCase().includes(query) ||
      monthlyReports[monthYear].some(
        (report) =>
          report.firstName?.toLowerCase().includes(query) ||
          report.lastName?.toLowerCase().includes(query) ||
          report.email?.toLowerCase().includes(query)
      )
    );
    const matchesStatus = statusFilter === 'all' || monthlyReports[monthYear].some(
      (report) => report.analyzingstatus === statusFilter
    );
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredMonthlyReports.length / itemsPerPage);
  const currentItems = filteredMonthlyReports.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, filteredMonthlyReports.length);
  const totalItems = filteredMonthlyReports.length;

  function handlePageChange(e, pageNum) {
    setCurrentPage(pageNum);
  }

  const handleAddOrder = () => {
    navigateTo('/information');
  };

  const handleOpenModal = (report) => {
    setSelectedReport(report);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedReport(null);
  };

  const generatePDF = (monthYear, monthReports) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`Exposure Report: ${monthYear}`, 10, 10);

    let y = 20;
    monthReports.forEach((report, index) => {
      doc.setFontSize(12);
      doc.text(`Report ${index + 1}`, 10, y);
      y += 10;

      doc.setFontSize(10);
      doc.text(`First Name: ${report.firstName || 'N/A'}`, 10, y);
      y += 5;
      doc.text(`Last Name: ${report.lastName || 'N/A'}`, 10, y);
      y += 5;
      doc.text(`Email: ${report.email || 'N/A'}`, 10, y);
      y += 5;
      doc.text(`Phone: ${report.phoneNumber || 'N/A'}`, 10, y);
      y += 5;
      doc.text(`Date of Birth: ${report.dateOfBirth ? new Date(report.dateOfBirth).toLocaleDateString() : 'N/A'}`, 10, y);
      y += 5;
      doc.text(`Gender: ${report.gender || 'N/A'}`, 10, y);
      y += 5;
      doc.text(`Alternative Names: ${report.alternativeNames?.join(', ') || 'N/A'}`, 10, y);
      y += 5;
      doc.text(`Addresses: ${report.addresses?.map(addr => `${addr.addressLine1}, ${addr.city}, ${addr.state}, ${addr.zipcode}, ${addr.country}`).join('; ') || 'N/A'}`, 10, y);
      y += 5;
      doc.text(`Exposures: ${report.exposures?.map(exp => `${exp.brokerName} - ${exp.riskFactor} - ${exp.deletionStatus}`).join('; ') || 'N/A'}`, 10, y);
      y += 5;
      doc.text(`Created At: ${new Date(report.createdAt).toLocaleString()}`, 10, y);
      y += 5;
      doc.text(`Updated At: ${new Date(report.updatedAt).toLocaleString()}`, 10, y);
      y += 10;

      // Add a new page if content exceeds the page height
      if (y > 280) {
        doc.addPage();
        y = 10;
      }
    });

    doc.save(`Exposure_Report_${monthYear}.pdf`);
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: isMobile ? '90%' : 500,
    maxHeight: '80vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    border: '1px solid #e5e5ef',
    borderRadius: '12px',
    boxShadow: 24,
    p: 3,
    fontFamily: '"Manrope", sans-serif',
  };

  return (
    <div className={Classes.hero} style={{ padding: activePage === '/' && '20px 0' }}>
      <div className={Classes.header}>
        <div>
          <h3>Reports</h3>
          <h5>View and download your monthly exposure reports.</h5>
        </div>
        <div className={Classes.headerRight}>
          <div className={Classes.searcCon}>
            <img src="/assets/images/search.png" alt="search icon" />
            <input
              type="text"
              placeholder="Search by month, name, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className={Classes.table}>
        <div className={Classes.tableWrapper}>
          <div className={Classes.tableHeader}>
            <div className={`${Classes.headerItem} ${Classes.headerCustomer}`}>
              <h5>Month</h5>
            </div>
            <div className={`${Classes.headerItem} ${Classes.headerSpendings}`}>
              <h5>Report Dates</h5>
            </div>
            <div className={`${Classes.headerItem} ${Classes.headerBookings}`}>
              <h5>Actions</h5>
            </div>
          </div>

          <div className={Classes.tableBody}>
            {loading ? (
              <SkeletonLoading
                length={itemsPerPage}
                height={isMobile ? '6rem' : '3.5rem'}
                width="100%"
                br="10px"
                padding="0.5rem"
              />
            ) : currentItems?.length > 0 ? (
              currentItems?.map((monthYear, idx) => {
                const reportsForMonth = monthlyReports[monthYear];
                const firstReport = reportsForMonth[0];
                const lastReport = reportsForMonth[reportsForMonth?.length - 1];

                return (
                  <TableRow 
                    key={idx} 
                    monthYear={monthYear} 
                    generatePDF={generatePDF} 
                    reportsForMonth={reportsForMonth} 
                    firstReport={firstReport} 
                    lastReport={lastReport} 
                  />
                );
              })
            ) : (
              <h5 className='lightText' style={{ margin: '1rem' }} >No reports found...</h5>
            )}
          </div>

          <div className={Classes.pagFooter}>
            <h5>
              {loading ? (
                <SkeletonLoading
                  height="1.2rem"
                  width="10rem"
                  br="4px"
                  padding="0"
                />
              ) : (
                `Showing ${startItem} to ${endItem} of ${totalItems} results`
              )}
            </h5>
            <Stack spacing={2} className={Classes.pagination}>
              {loading ? (
                <SkeletonLoading
                  height="1.2rem"
                  width="5rem"
                  br="4px"
                  padding="0"
                />
              ) : (
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  shape="rounded"
                  size={isMobile ? 'small' : 'medium'}
                />
              )}
            </Stack>
          </div>
        </div>
      </div>

      <Modal open={openModal} onClose={handleCloseModal}>
        <Box sx={modalStyle} className={Classes.modalContent}>
          <IconButton
            onClick={handleCloseModal}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {selectedReport && (
            <div className={Classes.exposedList}>
              <Typography
                variant="h6"
                className={Classes.modalTitle}
                sx={{ mb: 2, fontWeight: 600 }}
              >
                Report Details
              </Typography>
              <div className={Classes.modalSection}>
                <Typography variant="subtitle1" className={Classes.modalSubtitle}>
                  Personal Information
                </Typography>
                <ul className={Classes.exposedList}>
                  <li className={Classes.exposedItem}>
                    <strong>Name:</strong> {selectedReport.firstName || 'N/A'}{' '}
                    {selectedReport.lastName || ''}
                  </li>
                  <li className={Classes.exposedItem}>
                    <strong>Email:</strong> {selectedReport.email || 'N/A'}
                  </li>
                  <li className={Classes.exposedItem}>
                    <strong>Contact Email:</strong> {selectedReport.contactEmail || 'N/A'}
                  </li>
                  <li className={Classes.exposedItem}>
                    <strong>Phone:</strong> {selectedReport.phoneNumber || 'N/A'}
                  </li>
                  <li className={Classes.exposedItem}>
                    <strong>Date of Birth:</strong>{' '}
                    {selectedReport.dateOfBirth
                      ? new Date(selectedReport.dateOfBirth).toLocaleDateString()
                      : 'N/A'}
                  </li>
                  <li className={Classes.exposedItem}>
                    <strong>Gender:</strong> {selectedReport.gender || 'N/A'}
                  </li>
                  <li className={Classes.exposedItem}>
                    <strong>Alternative Names:</strong>{' '}
                    {selectedReport.alternativeNames?.length > 0
                      ? selectedReport.alternativeNames.join(', ')
                      : 'N/A'}
                  </li>
                </ul>
              </div>
            </div>
          )}
        </Box>
      </Modal>
    </div>
  );
}